import React, { useEffect, useState } from 'react';
import { DatePicker, Input } from 'antd';
import dayjs from 'dayjs';
import moment from 'moment/moment.js';
import { useSearchParams } from 'react-router-dom';
import Pagination from '../../../components/pagination/Pagination.jsx';
import TableWithSkeleton from '../../../components/table/TableWithSkeleton.jsx';
import columns from '../../../columns/ib/Commission.js';
import { presets } from '../../../utils/RangePickerPresets.js';
import { GetCommissionApi, GetCommissionlotApi } from '../../../api/request/ib/commission.js';

const { RangePicker } = DatePicker;

export default function CommissionMobile() {
  const [searchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecord, setTotalRecord] = useState(0);
  const [search, setSearch] = useState('');
  const [searchText, setSearchText] = useState('');
  const [sorter, setSorter] = useState({ SortBy: 'ibCommDate', SortOrder: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([]);
  const [lotdata, setDatalot] = useState({});
  const [overviewRange, setOverviewRange] = useState({ from: '', to: '' });
  const currentDate = moment(new Date()).format('YYYY/MM/DD');

  const getCommission = async () => {
    try {
      setIsLoading(true);
      const params = {
        PageNumber: page,
        PageSize: pageSize,
        SearchText: searchText,
        FromDate: overviewRange?.from,
        ToDate: overviewRange?.to,
        ...sorter,
      };
      const { data } = await GetCommissionApi(new URLSearchParams(params).toString());
      if (data?.success) {
        setData(
          (data?.data || []).map((item) => ({
            ...item,
            name: `${item?.firstName || ''} ${item?.lastName || ''}`.trim(),
            registeredDate: item?.deal_Date ? moment(item.deal_Date).format('DD-MM-YYYY LT') : null,
            ibCommDate: item?.ibCommDate ? moment(item.ibCommDate).format('DD-MM-YYYY LT') : null,
            ibCommission: `$ ${item?.ibCommission}`,
          }))
        );
        setTotalRecord(data?.totalRecords || 0);
      } else {
        setData([]);
        setTotalRecord(0);
      }
    } catch (error) {
      setData([]);
      setTotalRecord(0);
    } finally {
      setIsLoading(false);
    }
  };

  const getCommissionlot = async () => {
    try {
      const params = {
        fromDate: overviewRange?.from,
        toDate: overviewRange?.to,
      };
      const { data } = await GetCommissionlotApi(new URLSearchParams(params).toString());
      if (data?.success) {
        setDatalot(data?.data);
      }
    } catch (error) { }
  };

  useEffect(() => {
    if (searchParams?.get('filter')?.trim() === 'today') {
      const today = moment(new Date()).format('MM/DD/YYYY');
      setOverviewRange({ from: today, to: today });
    }
  }, []);

  useEffect(() => {
    getCommissionlot();
    getCommission();
  }, [page, pageSize, searchText, sorter, overviewRange]);

  const handleDateChange = (value) => {
    if (value && value.length === 2) {
      setOverviewRange({
        from: value[0].format('MM/DD/YYYY'),
        to: value[1].format('MM/DD/YYYY'),
      });
      setPage(1);
      return;
    }
    setOverviewRange({ from: '', to: '' });
    setPage(1);
  };

  const handleTableChange = (pagination, filters, tableSorter) => {
    if (tableSorter?.order) {
      setSorter({ SortBy: tableSorter?.field, SortOrder: tableSorter?.order });
    } else {
      setSorter({ SortBy: 'ibCommDate', SortOrder: '' });
    }
  };

  const disabled7DaysDate = (current, { from }) => {
    if (from) return Math.abs(current.diff(from, 'days')) >= 7;
    return false;
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl border border-gray-100 p-3 shadow-sm space-y-3">
        <RangePicker
          className="w-full border-primary py-2"
          disabledDate={disabled7DaysDate}
          onChange={handleDateChange}
          presets={presets}
          defaultValue={
            searchParams?.get('filter')?.trim() === 'today'
              ? [dayjs(currentDate, 'YYYY/MM/DD'), dayjs(currentDate, 'YYYY/MM/DD')]
              : null
          }
          format="DD-MM-YYYY"
        />
        <Input
          size="large"
          placeholder="Search commission"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onPressEnter={() => {
            setSearchText(search);
            setPage(1);
          }}
          className="rounded-xl"
          suffix={
            search ? (
              <button
                type="button"
                className="text-gray-400"
                onClick={() => {
                  setSearch('');
                  setSearchText('');
                  setPage(1);
                }}
              >
                Clear
              </button>
            ) : null
          }
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm">
          <p className="text-xs text-gray-500">Total Lot</p>
          <p className="text-xl font-semibold primary mt-1">{lotdata?.totalLot || 0}</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm">
          <p className="text-xs text-gray-500">Total Commission</p>
          <p className="text-xl font-semibold primary mt-1">$ {lotdata?.totalCommission || 0}</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <TableWithSkeleton columns={columns} data={data} loading={isLoading} onChange={handleTableChange} />
        </div>
      </div>

      <Pagination total={totalRecord} page={page} setPage={setPage} pageSize={pageSize} setPageSize={setPageSize} />
    </div>
  );
}
